import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api.js";
import { Users } from "lucide-react";
import SyncPppProfileByUsermanId from "../components/Customer/Ppp/SyncPppProfileByUsermanId.js";
import PppMenu from "../components/Customer/Ppp/PppMenu.js";

const PppProfiles = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const limit = 1000;
  const [totalPages, setTotalPages] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  // SERVER SIDE FILTER
  const [filters, setFilters] = useState({
    name: "",
    rate_limit: "",
    address_list: "",
    is_default: "",
    disabled: "",
  });

  // CLIENT SIDE QUICK SEARCH
  const [quickSearch, setQuickSearch] = useState("");

  const fetchPppProfiles = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await api.get(`/ppp-profile-by-device/${usermanId}`, {
        headers: { Authorization: token },
        params: {
          page,
          limit,
          name: filters.name || undefined,
          rate_limit: filters.rate_limit || undefined,
          address_list: filters.address_list || undefined,
          is_default: filters.is_default || undefined,
          disabled: filters.disabled || undefined,
        },
      });

      setData(res.data.data);
      setTotalPages(res.data.pagination.totalPages);
      setTotalBackend(res.data.pagination.total);
    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data PPP Profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (usermanId) fetchPppProfiles();
  }, [usermanId, page]);

  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearch = () => {
    setPage(1);
    fetchPppProfiles();
  };

  // QUICK SEARCH (CLIENT SIDE)
  const filteredData = data.filter((item) =>
    [
      item.name,
      item.rate_limit,
      item.address_list,
      item.local_address,
      item.remote_address,
    ]
      .join(" ")
      .toLowerCase()
      .includes(quickSearch.toLowerCase())
  );

  return (
    <div className="p-6 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-200">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6" />
          {deviceName} /{" "}
          <PppMenu userman_id={usermanId} device_name={deviceName} /> / Profiles
        </h1>

        <SyncPppProfileByUsermanId
          usermanId={usermanId}
          onSuccess={() => {
            setPage(1);
            fetchPppProfiles();
          }}
        />
      </div>

      {/* FILTER SECTION */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
        <input
          type="text"
          name="name"
          placeholder="Profile name"
          value={filters.name}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="text"
          name="rate_limit"
          placeholder="Rate limit"
          value={filters.rate_limit}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="text"
          name="address_list"
          placeholder="Address list"
          value={filters.address_list}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <select
          name="is_default"
          value={filters.is_default}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Default?</option>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>

        <select
          name="disabled"
          value={filters.disabled}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Status</option>
          <option value="0">Active</option>
          <option value="1">Disabled</option>
        </select>
      </div>

      <button
        onClick={handleSearch}
        className="mb-4 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
      >
        Search
      </button>

      {/* QUICK SEARCH */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Quick search"
          value={quickSearch}
          onChange={(e) => {
            setPage(1);
            setQuickSearch(e.target.value);
          }}
          className="w-full max-w-sm px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <div className="flex gap-4 text-sm">
          <span>
            Total MikroTik: <b>{totalBackend}</b>
          </span>
          <span>
            Showing: <b>{filteredData.length}</b>
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Rate Limit</th>
              <th className="px-4 py-2 text-left">Address List</th>
              <th className="px-4 py-2 text-center">Default</th>
              <th className="px-4 py-2 text-center">Disabled</th>
              <th className="px-4 py-2 text-center">Created</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr
                  key={row.id}
                  className="border-t dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2">{row.rate_limit || "-"}</td>
                  <td className="px-4 py-2">{row.address_list || "-"}</td>
                  <td className="px-4 py-2 text-center">
                    {row.is_default ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-slate-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {row.disabled ? (
                      <span className="text-red-500">Yes</span>
                    ) : (
                      <span className="text-green-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {new Date(row.created_at).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Prev
        </button>

        <span>
          Page {page} / {totalPages}
        </span>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PppProfiles;
